import { useEffect, useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import FilterRow from "./filter-row";
import { MinMaxInput } from "@/ui/min-max-input";
import { SwitchButton } from "@/ui/switch-button";
import { ExchangeButton, ExchangeName } from "@/ui/exchange-button";
import { ScreenerTokensParams } from "@api/token-screener/screener-tokens.ts";
import {
  getTokenScreenerDefaultFiltersBySelection,
  useTokenScreenerFilter,
} from "@providers/token-screener-filter/token-screener-filter-provider";

const TokensFiltersModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { filters, onFiltersUpdate, selection } = useTokenScreenerFilter();
  const [localFilters, setLocalFilters] =
    useState<ScreenerTokensParams["filters"]>(filters);

  const handleSubmit = (newFilters: ScreenerTokensParams["filters"]) => {
    onFiltersUpdate(newFilters);
  };

  const handleExchangeToggle = (exchange: ExchangeName) => {
    const currentExchanges: NonNullable<
      ScreenerTokensParams["filters"]
    >["exchange"] = localFilters?.exchange ?? {};
    const currentlyEnabled = currentExchanges[exchange] === true;

    const updatedExchanges: NonNullable<
      ScreenerTokensParams["filters"]
    >["exchange"] = {
      ...currentExchanges,
    };

    if (currentlyEnabled) {
      delete updatedExchanges[exchange];
    } else {
      updatedExchanges[exchange] = true;
    }

    setLocalFilters({
      ...localFilters,
      exchange: updatedExchanges,
    });
  };

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  return (
    <>
      <GradientBorderRadius
        gradient="default-border"
        radius="bigger"
        hover="r-100"
        className="h-full"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
        >
          Filters
        </button>
      </GradientBorderRadius>

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        className="w-[28.125rem] min-h-[34.25rem] py-[0.9375rem] px-[0.6250rem]"
      >
        <Modal.Title title="Filters" className="text-xs font-semibold" />
        <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
          <FilterRow title="Picture">
            <SwitchButton
              className="gap-[0.375rem]"
              value={localFilters?.picture ? "yes" : "no"}
              onChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  picture: value === "yes",
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Website">
            <SwitchButton
              className="gap-[0.375rem]"
              value={localFilters?.website ? "yes" : "no"}
              onChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  website: value === "yes",
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Twitter">
            <SwitchButton
              className="gap-[0.375rem]"
              value={localFilters?.twitter ? "yes" : "no"}
              onChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  twitter: value === "yes",
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Telegram">
            <SwitchButton
              className="gap-[0.375rem]"
              value={localFilters?.telegram ? "yes" : "no"}
              onChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  telegram: value === "yes",
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Price">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={localFilters?.price?.min ?? ""}
              maxValue={localFilters?.price?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  price: {
                    ...prev!.price!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  price: {
                    ...prev!.price!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Age">
            <MinMaxInput
              unitSide="right"
              text="h"
              layout="row"
              minValue={localFilters?.age?.min ?? ""}
              maxValue={localFilters?.age?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  age: {
                    ...prev!.age!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  age: {
                    ...prev!.age!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Txns">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.txns?.min ?? ""}
              maxValue={localFilters?.txns?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  txns: {
                    ...prev!.txns!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  txns: {
                    ...prev!.txns!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Wallets">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.makers?.min ?? ""}
              maxValue={localFilters?.makers?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  makers: {
                    ...prev!.makers!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  makers: {
                    ...prev!.makers!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Holders">
            <MinMaxInput
              layout="row"
              minValue={localFilters?.holders?.min ?? ""}
              maxValue={localFilters?.holders?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  holders: {
                    ...prev!.holders!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  holders: {
                    ...prev!.holders!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Volume">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={localFilters?.volume?.min ?? ""}
              maxValue={localFilters?.volume?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  volume: {
                    ...prev!.volume!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  volume: {
                    ...prev!.volume!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Liquidity">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={localFilters?.liquidity?.min ?? ""}
              maxValue={localFilters?.liquidity?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  liquidity: {
                    ...prev!.liquidity!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  liquidity: {
                    ...prev!.liquidity!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="Market Cap">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={localFilters?.marketCap?.min ?? ""}
              maxValue={localFilters?.marketCap?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  marketCap: {
                    ...prev!.marketCap!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  marketCap: {
                    ...prev!.marketCap!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="1H Change">
            <MinMaxInput
              unitSide="right"
              text="%"
              layout="row"
              minValue={localFilters?.priceChange1Hour?.min ?? ""}
              maxValue={localFilters?.priceChange1Hour?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  priceChange1Hour: {
                    ...prev!.priceChange1Hour!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  priceChange1Hour: {
                    ...prev!.priceChange1Hour!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow title="24H Change">
            <MinMaxInput
              unitSide="right"
              text="%"
              layout="row"
              minValue={localFilters?.priceChange24Hours?.min ?? ""}
              maxValue={localFilters?.priceChange24Hours?.max ?? ""}
              onMinChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  priceChange24Hours: {
                    ...prev!.priceChange24Hours!,
                    min: value === "" ? undefined : Number(value),
                  },
                }));
              }}
              onMaxChange={(value) => {
                setLocalFilters((prev: ScreenerTokensParams["filters"]) => ({
                  ...prev!,
                  priceChange24Hours: {
                    ...prev!.priceChange24Hours!,
                    max: value === "" ? undefined : Number(value),
                  },
                }));
              }}
            />
          </FilterRow>
          <FilterRow
            title={
              <div className="flex items-center gap-[0.3125rem]">Exchanges</div>
            }
          >
            <div className="grid grid-cols-2 gap-[0.25rem]">
              <ExchangeButton
                iconName="pump"
                title="Pumpfun"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("pump", localFilters?.exchange)}
              ></ExchangeButton>
              <ExchangeButton
                iconName="pumpAmm"
                title="Pumpfun AMM"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("pumpAmm", localFilters?.exchange)}
              ></ExchangeButton>
            </div>
          </FilterRow>
          <FilterRow title="">
            <div className="grid grid-cols-2 gap-[0.25rem]">
              <ExchangeButton
                iconName="raydium"
                title="Raydium"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("raydium", localFilters?.exchange)}
              ></ExchangeButton>
              <ExchangeButton
                iconName="launchlab"
                title="Launchlab"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("launchlab", localFilters?.exchange)}
              ></ExchangeButton>
            </div>
          </FilterRow>
          <FilterRow title="">
            <div className="grid grid-cols-2 gap-[0.25rem]">
              <ExchangeButton
                iconName="meteora"
                title="Meteora"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("meteora", localFilters?.exchange)}
              ></ExchangeButton>
              <ExchangeButton
                iconName="orca"
                title="Orca"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("orca", localFilters?.exchange)}
              ></ExchangeButton>
            </div>
          </FilterRow>
          <FilterRow title="">
            <div className="grid grid-cols-2 gap-[0.25rem]">
              <ExchangeButton
                iconName="fluxbeam"
                title="Fluxbeam"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("fluxbeam", localFilters?.exchange)}
              ></ExchangeButton>
              <ExchangeButton
                iconName="boop"
                title="Boop"
                onToggle={handleExchangeToggle}
                active={isExchangeActive("boop", localFilters?.exchange)}
              ></ExchangeButton>
            </div>
          </FilterRow>
        </div>
        <Modal.Footer>
          <Button
            title="Done"
            className="w-[9.4375rem] font-semibold text-xs"
            active={false}
            onClick={() => {
              handleSubmit(localFilters);
              setIsOpen(false);
            }}
          />

          <Button
            title="Clear"
            className="w-[9.4375rem] font-semibold text-xs"
            active={false}
            onClick={() => {
              if (
                selection.showStarred ||
                selection.dropdownValue === "history"
              ) {
                const value = { tokens: filters?.tokens };
                setLocalFilters(value);
                handleSubmit(value);
                return;
              }

              const defaultValue = getTokenScreenerDefaultFiltersBySelection(
                selection.dropdownValue
              );
              setLocalFilters(defaultValue);
              handleSubmit(defaultValue);
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

const isExchangeActive = (
  exchange: ExchangeName,
  exchanges:
    | NonNullable<ScreenerTokensParams["filters"]>["exchange"]
    | undefined
) => {
  return exchanges?.[exchange] === true;
};

export default TokensFiltersModal;
